import { FC } from "react";
import { MenuProps } from "../types";
import { useStoreState } from "../store/useStore";
import { useTranslations } from "use-intl";
import { stylesText, megaMenuGroupTitle } from "../styles";
import ArrowIcon from "@/nav-localized/assets/header/arrow.svg?react";
import { Languages, LanguagesType } from "../../types";
import clsx from "clsx";

const H5Menu: FC<{
  menus: MenuProps;
  itemsKey?: string;
  changePages: (label: string) => void;
  changeRouter: (item: MenuProps, e: React.MouseEvent<HTMLAnchorElement>) => void;
}> = (props) => {
  const { menus, changePages, changeRouter } = props;

  const { localeLink, theme, locale } = useStoreState();

  const t = useTranslations("header");

  const childRoute = (url: string, basePath: string) => {
    return `/${basePath}${localeLink}/${url}`;
  };

  const buildHref = (item: MenuProps) => {
    if (item.outbound) return item.url ?? "#";
    if (item.basePath) return childRoute(item.url!, item.basePath);
    return `${localeLink}/${item.url}`;
  };

  return (
    <div
      className="flex items-start flex-col w-[calc(100%_-_16px)]"
      onClick={() => changePages(menus.label as string)}
    >
      <div className="flex h-[24px] items-center text-[14px] cursor-pointer justify-between w-full">
        <i className={clsx("flex items-center text-[16px]", stylesText({ mode: theme }))}>
          {menus.icon && <menus.icon className={stylesText({ mode: theme })} />}
          <i className="ml-[12px] not-italic">
            {
              Languages[locale as LanguagesType].header[
                menus.label as keyof (typeof Languages)[LanguagesType]["header"]
              ]
            }
          </i>
        </i>
        <ArrowIcon
          className={clsx(
            "ml-[2px] w-[20px] h-[20px]",
            menus.drawer && "rotate-180",
            stylesText({ mode: theme })
          )}
        />
      </div>
      {menus.drawer && (
        <div className="w-full" onClick={(e) => e.stopPropagation()}>
          {menus.more
            ? (() => {
                // 标准化为数组列格式
                const columns = Array.isArray(menus.more)
                  ? menus.more
                  : Object.keys(menus.more || {}).map((key) => ({
                      [key]: (menus.more as { [key: string]: MenuProps[] })[key]
                    }));

                return columns.map((column, columnIndex) =>
                  Object.entries(column).map(([groupKey, items]) => {
                    if (!Array.isArray(items) || items.length === 0) return null;
                    // 跳过 __widget__ 组在移动端
                    if (groupKey === "__widget__") return null;

                    // __ 开头的非 widget key（如 __items__）渲染条目但不显示标题
                    const showGroupTitle = !groupKey.startsWith("__");

                    return (
                      <div key={`${columnIndex}-${groupKey}`}>
                        {/* 分组标题 */}
                        {showGroupTitle && (
                          <div
                            className={clsx(
                              megaMenuGroupTitle({ mode: theme }),
                              "px-[40px] pt-[16px] pb-[4px]"
                            )}
                          >
                            {t(groupKey)}
                          </div>
                        )}
                        {items.map((item, index) => (
                          <a
                            href={buildHref(item)}
                            key={`${columnIndex}-${index}`}
                            onClick={(e) => {
                              changeRouter?.(item, e);
                            }}
                          >
                            <div className="flex items-center px-[40px] cursor-pointer">
                              <div className="flex flex-col mt-[12px]">
                                <span
                                  className={clsx(
                                    "text-[14px] flex items-center leading-[20px]",
                                    stylesText({ mode: theme })
                                  )}
                                >
                                  {t(item.label)}
                                </span>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    );
                  })
                );
              })()
            : menus.items?.map((item, index) => {
                return (
                  <a
                    href={buildHref(item)}
                    key={index}
                    onClick={(e) => {
                      changeRouter?.(item, e);
                    }}
                  >
                    <div className="flex items-center px-[40px] cursor-pointer">
                      <div className="flex flex-col mt-[12px]">
                        <span
                          className={clsx(
                            "text-[14px] flex items-center leading-[20px]",
                            stylesText({ mode: theme })
                          )}
                        >
                          {t(item.label)}
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
        </div>
      )}
    </div>
  );
};

export default H5Menu;
